import {
  inject,
  ref,
  shallowRef,
  type InjectionKey,
  type Ref,
  type ShallowRef
} from 'vue'
import type { ConfiguredPortRef } from '@gat-ship/types'
import { getLocalPortRequest } from '@gat-ship/ref-data'

export interface PortDetailService {
  port: ShallowRef<ConfiguredPortRef | null>
  loadingPort: Ref<boolean>
  fetchPort: (locationCode: string | undefined) => Promise<void>
}

/**
 * Single-port detail datasource: resolves one tenant-configured local port by
 * location code via `GET /ref-data/geography/ports/local/{locationCode}` (the
 * URL is expected to change once the port ref-data subsystem settles).
 * Read-only — the endpoint carries no mutations or SignalR, so this is a plain
 * fetch with no commands namespace. HTTP failures propagate to the global
 * error pipeline (the datasource convention); the view falls back to its
 * "Port not found." state on a null port.
 */
export function usePortDetail(): PortDetailService {
  const port = shallowRef<ConfiguredPortRef | null>(null)
  const loadingPort = ref(false)

  async function fetchPort(locationCode: string | undefined) {
    if (!locationCode) {
      port.value = null
      return
    }
    loadingPort.value = true
    port.value = null
    try {
      port.value = await getLocalPortRequest(locationCode)
    } finally {
      loadingPort.value = false
    }
  }

  return { port, loadingPort, fetchPort }
}

/**
 * Injection key for the single-port context. Kept in this composable module —
 * not a `providers/index.ts` barrel — so `PortProvider` and the detail modal
 * can share it without forming a barrel↔member import cycle (the
 * companies/agency/currencies precedent).
 */
export const portDetailKey = Symbol(
  'port-detail'
) as InjectionKey<PortDetailService>

/** Inject the single-port context provided by a parent `PortProvider`. */
export function usePortDetailContext() {
  return inject(portDetailKey, undefined)
}
